import * as THREE from "three";
import { CRAFT_DURATION_MS } from "@ghostland/shared";
import { kaponHeightAt } from "./kaponTerrain.js";

/**
 * The crafting "channel" visuals, billboarded OVER THE PLAYER (like the mining pickaxe). When the
 * player starts processing at a station, a pixelated progress bar fills over 10s while a gear spins
 * above their head; the bar runs only while the player stands still (movement cancels it). A batch
 * is processed ONE UNIT AT A TIME: each unit runs its own 10s bar, sends a Craft message, and pops a
 * SUCCESS x1 / FAIL x1 text before the next unit begins.
 *
 * The spinning gear is also shown over REMOTE players who are crafting (synced `crafting` flag), via
 * the shared `CraftGear` class — so everyone sees them work, not just the crafter.
 */

const RESULT_MS = 1500; // how long the SUCCESS/FAIL text holds before the next unit (or finishing)

function pixCanvasTex(c: HTMLCanvasElement): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.magFilter = THREE.NearestFilter; t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/** A crisp, higher-resolution pixel-art gear (64×64): a toothed steel cog with bevelled teeth,
 *  spokes, and a hub. Drawn once and shared; spun via the sprite material's rotation. */
let gearTexCache: THREE.CanvasTexture | null = null;
export function makeGearTexture(): THREE.CanvasTexture {
  if (gearTexCache) return gearTexCache;
  const S = 64, cx = 32, cy = 32;
  const c = document.createElement("canvas"); c.width = c.height = S;
  const ctx = c.getContext("2d")!; ctx.imageSmoothingEnabled = false;
  const disc = (r: number, color: string): void => { ctx.fillStyle = color; ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill(); };
  // 10 chunky teeth around the rim (drawn as rotated blocks)
  const TEETH = 10;
  for (let i = 0; i < TEETH; i++) {
    const a = (i / TEETH) * Math.PI * 2;
    ctx.save(); ctx.translate(cx, cy); ctx.rotate(a);
    ctx.fillStyle = "#9aa3ad"; ctx.fillRect(-6, -30, 12, 12);      // tooth body
    ctx.fillStyle = "#c2cad3"; ctx.fillRect(-6, -30, 12, 3);       // tooth highlight (outer edge)
    ctx.fillStyle = "#6f7882"; ctx.fillRect(-6, -20, 12, 2);       // tooth shadow (root)
    ctx.restore();
  }
  disc(24, "#7c858f");   // outer rim shadow
  disc(22, "#aeb6bf");   // rim
  disc(19, "#8b939c");   // rim inner shadow
  disc(16, "#c6cdd5");   // face
  // spokes
  ctx.strokeStyle = "#7c858f"; ctx.lineWidth = 4;
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx + Math.cos(a) * 15, cy + Math.sin(a) * 15); ctx.stroke();
  }
  disc(8, "#aeb6bf");    // hub
  disc(5, "#5a626b");    // hub bore
  disc(4, "#3a4048");
  // a couple of single-pixel speculars for the "pixel art" sparkle
  ctx.fillStyle = "#e6ecf2"; ctx.fillRect(20, 18, 2, 2); ctx.fillRect(40, 26, 2, 2);
  gearTexCache = pixCanvasTex(c);
  return gearTexCache;
}

/** Render SUCCESS x1 / FAIL x1 pixel text into a canvas texture (green / red). */
function makeResultTexture(text: string, ok: boolean): THREE.CanvasTexture {
  const W = 128, H = 28;
  const c = document.createElement("canvas"); c.width = W; c.height = H;
  const ctx = c.getContext("2d")!; ctx.imageSmoothingEnabled = false;
  ctx.font = "bold 18px ui-monospace, monospace"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.lineWidth = 5; ctx.strokeStyle = "#000"; ctx.strokeText(text, W / 2, H / 2 + 1); // black pixel outline
  ctx.fillStyle = ok ? "#7bd66a" : "#e0584f";
  ctx.fillText(text, W / 2, H / 2 + 1);
  return pixCanvasTex(c);
}

/** A spinning gear billboarded over a crafter's head — the shared crafting animation. Used for the
 *  LOCAL player (inside CraftController, alongside the bar) and for REMOTE crafters (one per remote).
 *  Position `group` over the player each frame and call `spin(nowMs)`. */
export class CraftGear {
  readonly group = new THREE.Group();
  private readonly gear: THREE.Sprite;
  constructor() {
    // Same depth treatment as the player billboard: alphaTest + depthWrite so buildings OCCLUDE it
    // (no seeing it through walls) and it writes near depth, staying sharp under the world DoF.
    this.gear = new THREE.Sprite(new THREE.SpriteMaterial({ map: makeGearTexture(), transparent: true, alphaTest: 0.5, depthWrite: true }));
    this.gear.scale.set(0.42, 0.42, 1); this.gear.position.set(0, 1.9, 0); // small, tucked just above the bar
    this.group.add(this.gear);
  }
  spin(nowMs: number): void { this.gear.material.rotation = -(nowMs / 1000) * 3; }
  // NOTE: don't dispose the map — the gear texture is a shared, cached singleton (makeGearTexture).
  dispose(): void { (this.gear.material as THREE.SpriteMaterial).dispose(); }
}

/** One unit's lifecycle phase: filling the bar, awaiting the server result, or the batch is finished
 *  and the final result pop is fading out. */
type Phase = "fill" | "await" | "done";

export class CraftController {
  private readonly ui = new THREE.Group();
  private readonly barCanvas: HTMLCanvasElement;
  private readonly barTex: THREE.CanvasTexture;
  private readonly bar: THREE.Sprite;
  private readonly gearFx = new CraftGear();
  private readonly result: THREE.Sprite;

  private channel: { recipeId: string; remaining: number; cycleStart: number; durationMs: number } | null = null;
  private phase: Phase = "fill";
  private resultUntil = 0;

  constructor(scene: THREE.Scene) {
    this.barCanvas = document.createElement("canvas");
    this.barCanvas.width = 48; this.barCanvas.height = 10;
    this.barTex = pixCanvasTex(this.barCanvas);
    this.bar = new THREE.Sprite(new THREE.SpriteMaterial({ map: this.barTex, transparent: true, alphaTest: 0.5, depthWrite: true }));
    this.bar.scale.set(0.5, 0.1, 1); this.bar.position.set(0, 1.55, 0); // occluded by buildings, like the player

    // Result text floats just ABOVE the gear and fades on its own timer — it does NOT pause the
    // batch (the next unit starts immediately).
    this.result = new THREE.Sprite(new THREE.SpriteMaterial({ map: makeResultTexture("SUCCESS x1", true), transparent: true, alphaTest: 0.5, depthWrite: true }));
    this.result.scale.set(1.4, 0.31, 1); this.result.position.set(0, 2.3, 0); this.result.visible = false;

    this.ui.add(this.bar, this.gearFx.group, this.result);
    this.ui.visible = false;
    scene.add(this.ui);
  }

  /** Begin processing `quantity` units of `recipeId`, one cycle at a time (default 10s; pass `durationMs`
   *  for faster stations like the cooking pot's fries). */
  start(recipeId: string, quantity: number, nowMs: number, durationMs: number = CRAFT_DURATION_MS): void {
    this.channel = { recipeId, remaining: Math.max(1, Math.floor(quantity)), cycleStart: nowMs, durationMs };
    this.phase = "fill"; this.resultUntil = 0;
    this.bar.visible = true; this.gearFx.group.visible = true; this.result.visible = false;
    this.ui.visible = true;
  }

  /** Cancel the whole batch (e.g. the player moved). */
  cancel(): void {
    this.channel = null; this.resultUntil = 0;
    this.ui.visible = false; this.result.visible = false;
  }

  /** Cancellable phase: a unit's bar is filling. (NOT while a finished unit is in-flight, nor while
   *  the final result is fading out.) */
  isChanneling(): boolean { return this.channel != null && this.phase === "fill"; }
  /** Busy with a batch at all — drives the synced `crafting` flag + the "machine in use" prompt. */
  isCrafting(): boolean { return this.channel != null; }
  isBusy(): boolean { return this.channel != null; }

  /** Show the SUCCESS x1 / FAIL x1 pop for the unit the server just resolved, and IMMEDIATELY start
   *  the next unit (the text fades over the gear while the next bar fills). */
  showResult(succeeded: number, attempted: number, nowMs: number): void {
    if (!this.channel) return;
    this.channel.remaining = Math.max(0, this.channel.remaining - Math.max(1, attempted));
    const ok = succeeded > 0;
    const text = ok ? `SUCCESS x${succeeded}` : `FAIL x${Math.max(1, attempted)}`;
    const mat = this.result.material as THREE.SpriteMaterial;
    mat.map?.dispose(); mat.map = makeResultTexture(text, ok); mat.needsUpdate = true; mat.opacity = 1;
    this.result.scale.set(text.length > 8 ? 1.6 : 1.4, 0.31, 1);
    this.result.position.y = 2.3; this.result.visible = true;
    this.resultUntil = nowMs + RESULT_MS;
    if (this.channel.remaining > 0) { this.phase = "fill"; this.channel.cycleStart = nowMs; this.bar.visible = true; } // next unit, no pause
    else { this.phase = "done"; this.bar.visible = false; } // batch finished — let the last pop fade out
  }

  /**
   * Per-frame: keep the UI billboarded over the player, fill the current unit's bar + spin the gear.
   * Returns the unit to send (recipeId + quantity 1) when a bar completes, else null. The floating
   * result text fades independently; the batch ends once the final pop has faded.
   */
  tick(playerX: number, playerZ: number, nowMs: number): { recipeId: string; quantity: number } | null {
    if (!this.channel) return null;
    this.ui.position.set(playerX, kaponHeightAt(playerX, playerZ), playerZ);
    this.gearFx.spin(nowMs);

    // Float + fade the result pop on its own timer (overlaps the next unit's bar).
    if (this.result.visible) {
      const left = this.resultUntil - nowMs;
      (this.result.material as THREE.SpriteMaterial).opacity = Math.max(0, Math.min(1, left / 400));
      this.result.position.y = 2.3 + (RESULT_MS - left) / RESULT_MS * 0.4;
      if (left <= 0) {
        this.result.visible = false;
        if (this.phase === "done") { this.cancel(); return null; } // last pop faded — done
      }
    }

    if (this.phase === "fill") {
      const t = Math.min(1, (nowMs - this.channel.cycleStart) / this.channel.durationMs);
      this.drawBar(t);
      if (t >= 1) { this.phase = "await"; return { recipeId: this.channel.recipeId, quantity: 1 }; }
    }
    return null; // "await" (in-flight) and "done" (fading) just wait
  }

  private drawBar(t: number): void {
    const ctx = this.barCanvas.getContext("2d")!;
    const W = this.barCanvas.width, H = this.barCanvas.height;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#06120a"; ctx.fillRect(0, 0, W, H);          // backdrop
    ctx.fillStyle = "#1f3a22"; ctx.fillRect(1, 1, W - 2, H - 2);  // empty track
    ctx.fillStyle = "#74d86a"; ctx.fillRect(1, 1, Math.round((W - 2) * t), H - 2); // fill (leather green)
    ctx.fillStyle = "#0e1f12"; // border
    ctx.fillRect(0, 0, W, 1); ctx.fillRect(0, H - 1, W, 1); ctx.fillRect(0, 0, 1, H); ctx.fillRect(W - 1, 0, 1, H);
    this.barTex.needsUpdate = true;
  }
}
